import React, {
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { View } from "react-native";
import { Button, ButtonText } from "ui/gluestack/button";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { ReusableModal } from "ui/molecules/modal/ReusableModal";
import { FloorModel } from "app/services/api/types/locations/models/FloorModel";
import { FloorRequest } from "app/services/api/types/locations/requests/FloorRequest";

export interface EditFloorModalRef {
  show: (floor?: FloorModel) => void;
  hide: () => void;
}

type FloorFormData = Omit<FloorRequest, "id">;

interface EditFloorModalProps {
  onSave: (floorData: FloorFormData, floorId?: number) => void;
  isLoading?: boolean;
}

export const EditFloorModal = forwardRef<
  EditFloorModalRef,
  EditFloorModalProps
>(({ onSave, isLoading = false }, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const [floor, setFloor] = useState<FloorModel | undefined>();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<FloorFormData>({
    defaultValues: {
      name: "",
      floor_number: "",
      description: "",
    },
    mode: "onChange",
  });

  useImperativeHandle(
    ref,
    () => ({
      show: (showFloor?: FloorModel) => {
        setFloor(showFloor);
        setIsVisible(true);
      },
      hide: () => {
        setIsVisible(false);
      },
    }),
    [],
  );

  useEffect(() => {
    if (isVisible) {
      if (floor) {
        reset({
          name: floor.name,
          floor_number: floor.floor_number,
          description: floor.description,
        });
      } else {
        reset({
          name: "",
          floor_number: "",
          description: "",
        });
      }
    }
  }, [isVisible, floor, reset]);

  const handleSave = (data: FloorFormData) => {
    onSave(data, floor?.id);
  };

  const handleClose = () => {
    reset();
    setIsVisible(false);
  };

  const canSave = isValid && !isLoading;

  return (
    <ReusableModal
      isVisible={isVisible}
      onClose={handleClose}
      title={floor ? "Edit Floor" : "Add Floor"}
      hasCancelButton
    >
      <View className="space-y-4">
        <FormControlledInput
          id="floor-name"
          name="name"
          label="Floor Name"
          placeholder="e.g., Ground Floor, First Floor"
          control={control}
          isRequired
        />

        <FormControlledInput
          id="floor-number"
          name="floor_number"
          label="Floor Number"
          placeholder="e.g., 1, 2, 3"
          control={control}
          isRequired
        />

        <FormControlledInput
          id="floor-description"
          name="description"
          label="Description"
          placeholder="Optional description for the floor"
          control={control}
          isRequired={false}
        />
      </View>

      <View className="flex-row gap-3 mt-6">
        <Button
          variant="outline"
          onPress={handleClose}
          className="flex-1"
          disabled={isLoading}
        >
          <ButtonText>Cancel</ButtonText>
        </Button>
        <Button
          onPress={handleSubmit(handleSave)}
          className="flex-1"
          disabled={!canSave}
        >
          <ButtonText>
            {isLoading ? "Saving..." : floor ? "Update" : "Add"}
          </ButtonText>
        </Button>
      </View>
    </ReusableModal>
  );
});

EditFloorModal.displayName = "EditFloorModal";
